import { useContext, useRef, useState } from "react";
import { useReactFlow } from "reactflow";
import { DrawingContext } from "../../../../../../components/drawing-tool/DrawingTool";
import { DrawingForIngredientContext } from "../../../../../supplier_drawing_tool/DrawingToolForIngredient";
import { INTERNAL_TRANSPORT_GATE_CONFIG } from "../internalTransportGate.config";
import {
  InternalTransportGate,
  InternalTransportModalData,
  InternalTransportToolVariant,
  SaveDocumentDataHandle
} from "../internalTransport.types";
import { useInternalTransportActions } from "./useInternalTransportActions";
import { useInternalTransportApi } from "./useInternalTransportApi";
import { useInternalTransportForm } from "./useInternalTransportForm";
import { useInternalTransportSimulateCompare } from "./useInternalTransportSimulateCompare";

interface UseInternalTransportBaseScreenOptions {
  modalData: InternalTransportModalData;
  gate: InternalTransportGate;
  variant?: InternalTransportToolVariant;
}

export const useInternalTransportBaseScreen = ({
  modalData,
  gate,
  variant = "mapping"
}: UseInternalTransportBaseScreenOptions) => {
  const mappingContext = useContext(DrawingContext);
  const supplierContext = useContext(DrawingForIngredientContext);
  const drawingContext = variant === "supplier" ? supplierContext : mappingContext;

  const {
    nodeItemId,
    lockStatus = false,
    setNodeItem,
    setChartNodes,
    saveDrawing,
    chartEdges,
    chartNodes
  } = drawingContext as typeof mappingContext;

  const { farmGateId, processGateId, nodeItem, isDisconnected = false } = modalData;
  const gateId = gate === "farm" ? farmGateId : processGateId;
  const gateConfig = INTERNAL_TRANSPORT_GATE_CONFIG[gate];

  const notesRef = useRef<SaveDocumentDataHandle | null>(null);
  const { deleteElements, getEdges } = useReactFlow();

  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);

  const internalTransportId = nodeItem?.data?.reference?.internalTransportId || null;
  const connectedEdges = getEdges().filter(
    edge => edge.source === nodeItemId || edge.target === nodeItemId
  );

  const {
    data,
    status,
    postInternalTransport,
    putInternalTransport,
    deleteInternalTransport,
    originalTransportData,
    isPageLoading
  } = useInternalTransportApi({
    gate,
    variant,
    internalTransportId
  });

  const { originalSnapshot, isSimulateCompare } = useInternalTransportSimulateCompare({
    variant,
    originalTransportData
  });

  const form = useInternalTransportForm({
    data,
    status,
    variant,
    originalSnapshot,
    lockStatus,
    isDisconnected
  });

  const supportsNotes = variant === "mapping";
  const nameDisabled =
    variant === "simulate" && Boolean(internalTransportId && originalSnapshot?.transportName);

  const { onSubmit, handleDelete } = useInternalTransportActions({
    gate,
    variant,
    gateId,
    internalTransportId,
    nodeItem,
    chartNodes,
    chartEdges,
    connectedEdges,
    supportsNotes,
    existingTransportName: form.existingTransportName,
    originalSnapshot,
    notesRef,
    form,
    setNodeItem,
    setChartNodes,
    saveDrawing,
    deleteElements,
    postInternalTransport,
    putInternalTransport,
    deleteInternalTransport,
    setVisibleDeleteModal
  });

  return {
    isPageLoading,
    isFormDisabled: form.isFormDisabled,
    saveDisabled: form.saveDisabled,
    supportsNotes,
    control: form.control,
    form: form.form,
    handleSubmit: form.handleSubmit,
    onSubmit,
    notesRef,
    internalTransportId,
    transportationTypes: form.transportationTypes,
    transportationTypesFetching: form.transportationTypesFetching,
    transportationTypesError: form.transportationTypesError,
    refetchTransportationTypes: form.refetchTransportationTypes,
    visibleDeleteModal,
    setVisibleDeleteModal,
    handleDelete,
    lockStatus,
    deleteDisabled: lockStatus,
    saveTitle: internalTransportId ? "Update Transport" : "Save Transport",
    deleteTitle: gateConfig.deleteTitle,
    deleteSubtitle: gateConfig.deleteSubtitle,
    nameDisabled,
    isSimulateCompare,
    originalSnapshot
  };
};
